// Slot for job F-06. Wave 0 placed it; only F-06 edits this file.
// Host: each draft in an outbox batch (screens/approvals/OutboxBatch.tsx) (docs/specs/UPGRADES_2026_10.md).
//
// 4.25 The draft coach (the Lavender pattern) on Drafts to approve (4.10): the draft's 0-100 score as a ring with its
// three reasons (4.24), a phone-width preview, and four one-click rewrites: Shorter, Warmer, More direct and Use
// another fact. A rewrite is a new version, made by Claude in act and run through the same drafting checks and score
// as an overnight draft; if it cannot pass them the coach says why and nothing changes. Every version is kept: Undo,
// or a click on an earlier version, puts it back. Nothing here approves or sends: the host's Approve does, with
// whichever version is current.
//
// Calm by default (J-03, 18 Oct 2026): the host passes `detail` from its per-card "More", so an approve-all day shows
// only the score. The reasons to fix, the rewrite chips, Phone view and the versions show once More is open.
//
// The host shows the draft as it was loaded, so once a rewrite is current the coach shows the current version in its
// place (draft-coach.css hides the host's copy and its Edit button with :has(), and the coach offers "Edit this
// version", saved as a version too). While the owner has unsaved edits of their own, the chips wait, so a rewrite
// never overwrites what they typed.
//
// The learning base (31 Oct 2026): beside the chips, "Or tell Ogma what to change" (a note of up to 300 characters)
// rewrites the draft to the owner's own instruction; after it, "Make this a rule" keeps it for this campaign or every
// campaign (add_instruction). "Why this draft" (WhyThisDraft) says what the draft chose and why, from its ledger.
import { useContext, useEffect, useId, useState } from "react";
import { Check, CircleAlert, Smartphone, Undo2 } from "lucide-react";
import { addInstruction, readWhy, whyText } from "../../lib/learning";
import "../find/whats-working.css";
import { WorkspaceContext } from "../../lib/session";
import {
  CHIPS, label, loadCoach, MAX_NOTE, phonePreview, restoreVersion, reviseDraft, wordCount,
  type ChipKey, type CoachScore, type CoachState,
} from "../../lib/draftCoach";
import type { Crm } from "../../lib/crm";
import "./draft-coach.css";

interface Item {
  id?: unknown;
  channel?: unknown;
  status?: unknown;
  subject?: unknown;
  body?: unknown;
  person?: { full_name?: string | null; email?: string | null } | null;
  play_id?: unknown;
}

/** The draft's campaign: from the approval card's row, else from its ledger. */
const campaignOf = (item: Item, s: CoachState): string | null =>
  typeof item.play_id === "string" && item.play_id ? item.play_id : readWhy(s.ledger)?.play_id ?? null;

const BAND_WORDS = { strong: "strong", good: "good", weak: "needs work" } as const;

export function ScoreRing({ score }: { score: CoachScore }) {
  const r = 17;
  const c = 2 * Math.PI * r;
  return (
    <span className={`dc-ring dc-${score.band}`} role="img" aria-label={`Score ${score.score} out of 100, ${BAND_WORDS[score.band]}`}>
      <svg viewBox="0 0 40 40" width="44" height="44" aria-hidden="true">
        <circle cx="20" cy="20" r={r} className="dc-ring-track" />
        <circle cx="20" cy="20" r={r} className="dc-ring-value" strokeDasharray={`${(c * score.score) / 100} ${c}`} transform="rotate(-90 20 20)" />
      </svg>
      <span className="dc-ring-figure" aria-hidden="true">{score.score}</span>
    </span>
  );
}

function PhonePreview({ state, to }: { state: CoachState; to: string }) {
  const p = phonePreview(state.subject, state.body);
  return (
    <div className="dc-phone" aria-label="How it looks on a phone">
      <div className="dc-phone-inbox">
        <strong className="dc-phone-from">{to}</strong>
        <span className="dc-phone-subject">{p.subject}</span>
        <span className="dc-phone-line">{p.preview}</span>
      </div>
      <div className="dc-phone-body">{state.body}</div>
    </div>
  );
}

/** "first draft" or "version 2, shorter": plain words for which version is current. */
export function versionWords(n: number, instruction: string): string {
  if (instruction === "original") return n <= 1 ? "Ogma's first draft" : `version ${n}, Ogma's first draft`;
  return `version ${n}, ${label(instruction).toLowerCase()}`;
}

/** "Why this draft": one collapsed line with the choice and its reason, opening to the rules and lessons used. */
export function WhyThisDraft({ ledger }: { ledger: unknown }) {
  const [open, setOpen] = useState(false);
  const uid = useId();
  const w = readWhy(ledger);
  if (!w) return null;
  const used = [...w.instructions.map((s) => ({ key: `i-${s}`, text: `Your rule: ${s}` })), ...w.lessons.map((l) => ({ key: `l-${l.id}`, text: l.statement }))];
  return (
    <div className="draft-whyline">
      <button type="button" aria-expanded={open} aria-controls={`${uid}-why`} onClick={() => setOpen(!open)}>
        <strong>Why this draft:</strong> {whyText(w)}{used.length ? ` · ${used.length} used` : ""}
      </button>
      {open && (
        <div id={`${uid}-why`}>
          {used.length ? <ul aria-label="Rules and lessons used">{used.map((u) => <li key={u.key}>{u.text}</li>)}</ul>
            : <p className="muted">No rules or lessons yet.</p>}
        </div>
      )}
    </div>
  );
}

/** "Make this a rule": the note just used, kept for this campaign or every campaign. */
function MakeRule({ crm, note, campaign }: { crm: Crm; note: string; campaign: string | null }) {
  const [state, setState] = useState<"idle" | "choose" | "busy" | "done">("idle");
  const [err, setErr] = useState<string | null>(null);
  const save = async (scope: string | null) => {
    setState("busy");
    setErr(null);
    try {
      await addInstruction(crm, note, scope);
      setState("done");
    } catch (e) {
      setErr((e as Error).message);
      setState("choose");
    }
  };
  if (state === "done") return <p className="dc-rule" role="status">Saved as a rule. See it on What's working.</p>;
  return (
    <div className="dc-rule">
      {state === "idle" ? (
        <button type="button" className="link-btn" onClick={() => setState("choose")}>Make this a rule</button>
      ) : (
        <>
          <span>Make this a rule:</span>
          {campaign && <button type="button" className="btn btn-small" disabled={state === "busy"} onClick={() => void save(campaign)}>For this campaign</button>}
          <button type="button" className="btn btn-small" disabled={state === "busy"} onClick={() => void save(null)}>For every campaign</button>
          <button type="button" className="link-btn" onClick={() => setState("idle")}>Cancel</button>
        </>
      )}
      {err && <span className="is-bad" role="alert">{err}</span>}
    </div>
  );
}

export function DraftCoach({ item, detail = true }: { item: unknown; detail?: boolean }) {
  const ws = useContext(WorkspaceContext);
  const crm = ws?.crm as Crm | null | undefined;
  const d = (item ?? {}) as Item;
  const id = typeof d.id === "string" ? d.id : null;
  const usable = Boolean(crm && typeof crm.read === "function" && id && d.channel === "email");
  if (!usable) return null;
  return <Coach crm={crm!} id={id!} item={d} detail={detail} />;
}

function Coach({ crm, id, item, detail }: { crm: Crm; id: string; item: Item; detail: boolean }) {
  const uid = useId();
  const [state, setState] = useState<CoachState | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  const [phone, setPhone] = useState(false);
  const [undoTo, setUndoTo] = useState<number | null>(null);
  const [editing, setEditing] = useState<{ subject: string; body: string } | null>(null);
  const [note, setNote] = useState("");
  const [ruleFrom, setRuleFrom] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    loadCoach(crm, id).then((s) => live && (setState(s), setLoaded(true)), () => live && setLoaded(true));
    return () => {
      live = false;
    };
  }, [crm, id]);

  if (!loaded || !state || state.status !== "awaiting_approval") return null;
  const revised = state.body !== String(item.body ?? "") || (state.subject ?? "") !== String(item.subject ?? "");
  const to = item.person?.full_name || item.person?.email || "Them";
  const current = versionWords(state.version, state.instruction);

  const run = async (key: ChipKey | "note" | "by_hand", own?: { subject: string; body: string } | { note: string }) => {
    setBusy(key);
    setMsg(null);
    setRuleFrom(null);
    try {
      const r = await reviseDraft(crm, id, key, state.version, own);
      if (!r.saved || !r.state) {
        const why = (r.reasons.join("; ") || "the new version did not pass the checks").replace(/\.+$/, "");
        setMsg({ text: `Couldn't rewrite it: ${why}. The draft is unchanged.`, tone: "bad" });
        return;
      }
      setUndoTo(state.version);
      setState(r.state);
      setEditing(null);
      if (own && "note" in own) {
        setNote("");
        setRuleFrom(own.note);
      }
      const flagged = key === "by_hand" && r.flagged.length ? ` A check flagged: ${r.flagged.join("; ")}.` : "";
      const sc = r.state.score ? `, score ${r.state.score.score}` : "";
      setMsg({ text: `Version ${r.state.version} (${label(key)}${sc}) saved. It still needs your approval.${flagged}`, tone: "ok" });
    } catch (e) {
      setMsg({ text: (e as Error).message, tone: "bad" });
    } finally {
      setBusy(null);
    }
  };

  const restore = async (n: number) => {
    setBusy(`v${n}`);
    setMsg(null);
    try {
      const s = await restoreVersion(crm, id, n, state.version);
      if (s) {
        setUndoTo(state.version);
        setState(s);
        setEditing(null);
        setMsg({ text: `Back to ${versionWords(n, s.instruction)}. It still needs your approval.`, tone: "ok" });
      }
    } catch (e) {
      setMsg({ text: (e as Error).message, tone: "bad" });
    } finally {
      setBusy(null);
    }
  };

  const all = [...state.versions, { n: state.version, instruction: state.instruction, score: state.score?.score ?? null, current: true }]
    .sort((a, b) => a.n - b.n);

  return (
    <div className={`draft-coach${detail ? "" : " is-calm"}`} data-revised={revised ? "true" : undefined} aria-labelledby={`${uid}-title`}>
      <div className="dc-head">
        {state.score ? <ScoreRing score={state.score} /> : <span className="dc-ring dc-none muted small">No score</span>}
        <div className="dc-summary">
          <span id={`${uid}-title`} className="dc-title">
            Draft coach{state.score ? `: ${BAND_WORDS[state.score.band]}` : ""}
            {detail && <span className="muted small"> · {current} · {wordCount(state.body)} words</span>}
          </span>
          {detail && state.score && state.score.reasons.length > 0 && (
            <ul className="dc-reasons">
              {state.score.reasons.slice(0, 3).map((r) => (
                <li key={r.key + r.text} className={r.ok ? "is-ok" : "is-fix"}>
                  {r.ok ? <Check size={14} aria-hidden="true" /> : <CircleAlert size={14} aria-hidden="true" />}
                  <span className="sr-only">{r.ok ? "Good: " : "To fix: "}</span>
                  {r.text}
                </li>
              ))}
            </ul>
          )}
        </div>
        {detail && (
          <button type="button" className="btn btn-small dc-phone-toggle" aria-pressed={phone} onClick={() => setPhone(!phone)}>
            <Smartphone size={14} aria-hidden="true" /> Phone view
          </button>
        )}
      </div>

      <WhyThisDraft ledger={state.ledger} />

      {revised && !editing && (
        <div className="dc-message">
          <p className="review-subject">{state.subject || <span className="muted">(no subject)</span>}</p>
          <div className="review-body">{state.body}</div>
          <button type="button" className="btn btn-small" disabled={busy !== null}
                  onClick={() => setEditing({ subject: state.subject ?? "", body: state.body })}>
            Edit this version
          </button>
        </div>
      )}
      {editing && (
        <div className="dc-edit">
          <label className="field" htmlFor={`${uid}-subject`}>
            <span className="field-label">Subject</span>
            <input id={`${uid}-subject`} value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} />
          </label>
          <label className="field" htmlFor={`${uid}-body`}>
            <span className="field-label">Message</span>
            <textarea id={`${uid}-body`} rows={Math.max(6, editing.body.split("\n").length + 1)} value={editing.body}
                      onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
          </label>
          <div className="fold-actions">
            <button type="button" className="btn btn-primary btn-small" disabled={busy !== null || !editing.body.trim()}
                    onClick={() => void run("by_hand", editing)}>
              {busy === "by_hand" ? "Saving…" : "Save as a version"}
            </button>
            <button type="button" className="btn btn-small" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {detail && phone && <PhonePreview state={state} to={to} />}

      {detail && (
      <div className="dc-chips" role="group" aria-label="Rewrite this draft in one click">
        {CHIPS.map((c) => (
          <button key={c.key} type="button" className="btn btn-small dc-chip" disabled={busy !== null || editing !== null}
                  aria-busy={busy === c.key} onClick={() => void run(c.key)}>
            {busy === c.key ? "Rewriting…" : c.label}
          </button>
        ))}
        {undoTo !== null && all.some((v) => v.n === undoTo && !("current" in v)) && (
          <button type="button" className="btn btn-small dc-undo" disabled={busy !== null} onClick={() => void restore(undoTo)}>
            <Undo2 size={14} aria-hidden="true" /> Undo
          </button>
        )}
      </div>
      )}
      {detail && (
        <form className="dc-note" onSubmit={(e) => { e.preventDefault(); if (note.trim()) void run("note", { note: note.trim() }); }}>
          <input aria-label="Or tell Ogma what to change" placeholder="Or tell Ogma what to change" maxLength={MAX_NOTE} value={note}
                 disabled={busy !== null || editing !== null} onChange={(e) => setNote(e.target.value)} />
          <button type="submit" className="btn btn-small" disabled={busy !== null || editing !== null || !note.trim()} aria-busy={busy === "note"}>
            {busy === "note" ? "Rewriting…" : "Rewrite"}
          </button>
        </form>
      )}
      {detail && ruleFrom && <MakeRule key={ruleFrom} crm={crm} note={ruleFrom} campaign={campaignOf(item, state)} />}
      {detail && <p className="dc-edited-note muted small">You have edited this draft: approve or undo your edits before a one-click rewrite.</p>}

      {detail && all.length > 1 && (
        <div className="dc-versions" role="group" aria-label="Versions of this draft">
          <span className="muted small">Versions:</span>
          {all.map((v) => {
            const isCurrent = "current" in v;
            return (
              <button key={v.n} type="button" className={`btn btn-small dc-version${isCurrent ? " is-current" : ""}`}
                      aria-pressed={isCurrent} disabled={isCurrent || busy !== null} onClick={() => void restore(v.n)}
                      title={isCurrent ? "The version you will approve" : `Go back to version ${v.n}`}>
                {v.n} · {label(v.instruction)}{v.score !== null ? ` · ${v.score}` : ""}
              </button>
            );
          })}
        </div>
      )}
      <p className={`dc-status small${msg?.tone === "bad" ? " is-bad" : ""}`} role="status" aria-live="polite">{msg?.text ?? ""}</p>
    </div>
  );
}
